import { Element, ElementContent, Nodes, Parent } from 'hast';
import { PLACEHOLDER_CODE_BASE } from '../FormattedText/encodeTokenPlaceholders';
import { FormattedTextToken } from '../FormattedText/props';

/** The attribute a token span carries: which placeholder, by index. */
export const TOKEN_ATTRIBUTE = 'data-token';

/** The same attribute as hast spells it once parsed, which is how it reaches a React component. */
export const TOKEN_PROPERTY = 'dataToken';

/**
 * Turns the placeholder characters back into elements the renderer can draw as mentions,
 * hashtags and links.
 *
 * A chat message arrives with its tokens already found by the backend, so this never re-detects
 * them. `encodeTokenPlaceholders` swaps each one for a single private-use character before the
 * markdown is parsed — one character is indivisible, so no parser can split `@nathan.fitz` down
 * the middle or find emphasis inside a URL. This runs afterwards and splits every text node back
 * apart on those characters, leaving a `<span data-token="n">` where each one was.
 *
 * Code is the exception: a mention inside backticks is text about a mention, not a mention, so
 * inside `code` and `pre` the placeholders are simply written back as the characters they stood
 * for. That matches which tokens the message gets a link preview for (`filterTokensOutsideCode`).
 */
export function rehypeTokenPlaceholders(options: {
  tokenByCharCode: Map<number, FormattedTextToken>;
}) {
  return (tree: Nodes) => {
    if (options.tokenByCharCode.size > 0) {
      splitPlaceholders(tree, options.tokenByCharCode, false);
    }
  };
}

function splitPlaceholders(
  node: Nodes,
  tokenByCharCode: Map<number, FormattedTextToken>,
  isInsideCode: boolean,
) {
  if (!('children' in node)) {
    return;
  }

  const parent = node as Parent;
  const children: ElementContent[] = [];

  for (const child of parent.children as ElementContent[]) {
    if (child.type === 'text') {
      children.push(...splitText(child.value, tokenByCharCode, isInsideCode));
      continue;
    }

    const childIsCode =
      isInsideCode ||
      (child.type === 'element' && (child.tagName === 'code' || child.tagName === 'pre'));
    splitPlaceholders(child, tokenByCharCode, childIsCode);
    children.push(child);
  }

  parent.children = children;
}

function splitText(
  value: string,
  tokenByCharCode: Map<number, FormattedTextToken>,
  isInsideCode: boolean,
): ElementContent[] {
  const out: ElementContent[] = [];
  let plain = '';

  const flush = () => {
    if (plain) {
      out.push({ type: 'text', value: plain });
      plain = '';
    }
  };

  for (const character of value) {
    const code = character.codePointAt(0)!;
    const token = tokenByCharCode.get(code);

    if (!token) {
      plain += character;
      continue;
    }

    if (isInsideCode) {
      plain += token.value;
      continue;
    }

    flush();
    out.push(tokenElement(code, token.value));
  }

  flush();
  return out;
}

function tokenElement(code: number, value: string): Element {
  return {
    type: 'element',
    tagName: 'span',
    properties: { [TOKEN_PROPERTY]: String(code - PLACEHOLDER_CODE_BASE) },
    children: [{ type: 'text', value }],
  };
}
